import { create } from 'zustand';
import { announcementService } from '@services/announcementService';
import type {
  Announcement,
  AdminAnnouncement,
  PaginatedResponse,
} from '../types/ticket';

interface AnnouncementStore {
  // Data
  announcements: Announcement[];
  announcementPagination: {
    count: number;
    total_pages: number;
    current_page: number;
  } | null;
  unreadAnnouncementCount: number;
  latestUnreadAnnouncement: Announcement | null;
  adminAnnouncements: AdminAnnouncement[];
  adminAnnouncementPagination: {
    count: number;
    total_pages: number;
    current_page: number;
  } | null;

  // Loading states
  loading: boolean;
  error: string | null;

  // User actions
  fetchAnnouncements: (page?: number) => Promise<void>;
  fetchUnreadAnnouncements: () => Promise<void>;
  fetchAnnouncementUnreadCount: () => Promise<void>;
  markAnnouncementRead: (id: string) => Promise<void>;
  markAllAnnouncementsRead: () => Promise<void>;

  // Admin actions
  adminFetchAnnouncements: (params?: Record<string, any>) => Promise<void>;
  adminCreateAnnouncement: (data: {
    title: string;
    content: string;
    type?: string;
    target_type?: string;
    target_level_min?: number | null;
    target_level_max?: number | null;
    priority?: string;
    is_pinned?: boolean;
    is_published?: boolean;
  }) => Promise<AdminAnnouncement>;
  adminFetchAnnouncement: (id: string) => Promise<AdminAnnouncement>;
  adminUpdateAnnouncement: (id: string, data: any) => Promise<AdminAnnouncement>;
  adminDeleteAnnouncement: (id: string) => Promise<void>;
  adminPublishAnnouncement: (id: string) => Promise<void>;
  adminUnpublishAnnouncement: (id: string) => Promise<void>;

  // Utility
  clearError: () => void;
}

export const useAnnouncementStore = create<AnnouncementStore>()((set, get) => ({
  announcements: [],
  announcementPagination: null,
  unreadAnnouncementCount: 0,
  latestUnreadAnnouncement: null,
  adminAnnouncements: [],
  adminAnnouncementPagination: null,
  loading: false,
  error: null,

  // User actions
  fetchAnnouncements: async (page) => {
    set({ loading: true, error: null });
    try {
      const result = await announcementService.getAnnouncements({ page });
      set({
        announcements: result.results,
        announcementPagination: {
          count: result.count,
          total_pages: result.total_pages,
          current_page: result.current_page,
        },
        loading: false,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取公告列表失败', loading: false });
    }
  },

  fetchUnreadAnnouncements: async () => {
    try {
      const items = await announcementService.getUnreadAnnouncements();
      set({ latestUnreadAnnouncement: items.length > 0 ? items[0] : null });
    } catch (error: any) {
      console.error('Failed to fetch unread announcements:', error);
    }
  },

  fetchAnnouncementUnreadCount: async () => {
    try {
      const count = await announcementService.getAnnouncementUnreadCount();
      set({ unreadAnnouncementCount: count });
    } catch (error: any) {
      console.error('Failed to fetch announcement unread count:', error);
    }
  },

  markAnnouncementRead: async (id) => {
    try {
      await announcementService.markAnnouncementRead(id);
      set(state => ({
        announcements: state.announcements.map(a =>
          a.id === id ? { ...a, is_read: true } : a
        ),
        unreadAnnouncementCount: Math.max(0, state.unreadAnnouncementCount - 1),
        latestUnreadAnnouncement:
          state.latestUnreadAnnouncement?.id === id ? null : state.latestUnreadAnnouncement,
      }));
    } catch (error: any) {
      console.error('Failed to mark announcement read:', error);
    }
  },

  markAllAnnouncementsRead: async () => {
    try {
      const { announcements } = get();
      const unreadIds = announcements.filter(a => !a.is_read).map(a => a.id);
      await Promise.all(unreadIds.map(id => announcementService.markAnnouncementRead(id)));
      set(state => ({
        announcements: state.announcements.map(a => ({ ...a, is_read: true })),
        unreadAnnouncementCount: 0,
        latestUnreadAnnouncement: null,
      }));
    } catch (error: any) {
      console.error('Failed to mark all announcements read:', error);
    }
  },

  // Admin actions
  adminFetchAnnouncements: async (params) => {
    set({ loading: true, error: null });
    try {
      const result = await announcementService.adminGetAnnouncements(params);
      set({
        adminAnnouncements: result.results,
        adminAnnouncementPagination: {
          count: result.count,
          total_pages: result.total_pages,
          current_page: result.current_page,
        },
        loading: false,
      });
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取公告列表失败', loading: false });
    }
  },

  adminCreateAnnouncement: async (data) => {
    set({ loading: true, error: null });
    try {
      const announcement = await announcementService.adminCreateAnnouncement(data);
      set(state => ({
        adminAnnouncements: [announcement, ...state.adminAnnouncements],
        loading: false,
      }));
      return announcement;
    } catch (error: any) {
      const msg = error.response?.data?.message || '创建公告失败';
      set({ error: msg, loading: false });
      throw error;
    }
  },

  adminFetchAnnouncement: async (id) => {
    try {
      return await announcementService.adminGetAnnouncement(id);
    } catch (error: any) {
      set({ error: error.response?.data?.message || '获取公告详情失败' });
      throw error;
    }
  },

  adminUpdateAnnouncement: async (id, data) => {
    try {
      const announcement = await announcementService.adminUpdateAnnouncement(id, data);
      set(state => ({
        adminAnnouncements: state.adminAnnouncements.map(a =>
          a.id === id ? announcement : a
        ),
      }));
      return announcement;
    } catch (error: any) {
      set({ error: error.response?.data?.message || '更新公告失败' });
      throw error;
    }
  },

  adminDeleteAnnouncement: async (id) => {
    try {
      await announcementService.adminDeleteAnnouncement(id);
      set(state => ({
        adminAnnouncements: state.adminAnnouncements.filter(a => a.id !== id),
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.message || '删除公告失败' });
      throw error;
    }
  },

  adminPublishAnnouncement: async (id) => {
    try {
      const announcement = await announcementService.adminPublishAnnouncement(id);
      set(state => ({
        adminAnnouncements: state.adminAnnouncements.map(a =>
          a.id === id ? announcement : a
        ),
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.message || '发布公告失败' });
      throw error;
    }
  },

  adminUnpublishAnnouncement: async (id) => {
    try {
      const announcement = await announcementService.adminUnpublishAnnouncement(id);
      set(state => ({
        adminAnnouncements: state.adminAnnouncements.map(a =>
          a.id === id ? announcement : a
        ),
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.message || '取消发布公告失败' });
      throw error;
    }
  },

  // Utility
  clearError: () => set({ error: null }),
}));
